/**
 * Subscription Manager Component
 * Clean, cohesive subscription management for the account page
 */

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardTitle } from '@/components/ui/card';

import { useToast } from '@/components/ui/use-toast';
import { SUBSCRIPTION_PLANS } from '@/constants/pricing';
import { useLicense } from '@/hooks/useLicense';
import { useInvalidateLicensePlans } from '@/hooks/useLicensePlans';
import { useProductMeta } from '@/hooks/meta/useProductMeta';
import { supabase } from '@/services/supabase/supabase';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import {
  AlertCircle,
  Check,
  Crown,
  Sparkles,
  X,
  Zap,
  ShieldCheck,
  Coins,
  History,
  CreditCard
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { RazorpayPayment } from './razorpay-payment';

interface SubscriptionManagerProps {
  onUpgradeSuccess?: () => void;
}

export function SubscriptionManager({ onUpgradeSuccess }: SubscriptionManagerProps) {
  const { licenseDetails, refreshLicense } = useLicense();
  const invalidatePlans = useInvalidateLicensePlans();
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const { toast } = useToast();
  const { tenant } = useProductMeta();

  const currentPlan = licenseDetails?.plan || 'Free';
  const isTrialUser = licenseDetails?.status === 'trial';

  // Force refresh plans cache on mount to ensure we have latest data
  useEffect(() => {
    invalidatePlans();
  }, [invalidatePlans]);

  // Handle unsubscribe functionality
  const handleUnsubscribe = async () => {
    if (!licenseDetails || currentPlan === 'Free' || currentPlan === 'Visionary') { // Adjusted check
      return;
    }

    setIsUnsubscribing(true);

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        throw new Error('Please log in to continue');
      }

      if (!tenant?.id) {
        throw new Error('Unable to determine account context');
      }
      const tenantId = tenant.id;

      // Call the payment processor to handle unsubscription
      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/payment-processor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`,
          'x-tenant-id': tenantId
        },
        body: JSON.stringify({
          action: 'cancel_subscription'
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to cancel subscription');
      }

      toast({
        title: "Subscription Cancelled",
        description: "Your subscription has been cancelled. You'll continue to have access until your current billing period ends.",
      });

      // Refresh license data
      if (refreshLicense) {
        await refreshLicense();
      }

      if (onUpgradeSuccess) {
        onUpgradeSuccess();
      }

    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : 'Failed to cancel subscription',
        variant: "destructive",
      });
    } finally {
      setIsUnsubscribing(false);
    }
  };


  const renderPlanCard = (plan: any, isCurrentPlan: boolean = false) => {
    const isPro = plan.id === 'plus';
    const currentPrice = billingCycle === 'monthly' ? plan.price.monthly : plan.price.yearly;
    const savings = billingCycle === 'yearly' ? (plan.price.monthly * 12) - plan.price.yearly : 0;

    return (
      <motion.div
        key={plan.id}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
        className="h-full flex flex-col"
      >
        <Card className={cn(
          "relative h-full transition-all duration-300 overflow-hidden border flex flex-col",
          isCurrentPlan
            ? "border-primary/50 bg-primary/5 shadow-lg shadow-primary/5"
            : "border-border hover:border-primary/30 hover:shadow-lg hover:-translate-y-1 group"
        )}>
          {/* Status Badge */}
          <div className="absolute top-0 right-0 p-4 z-10">
            {isCurrentPlan ? (
              <Badge variant="secondary" className="bg-background/80 backdrop-blur text-foreground border-border shadow-sm">
                Current Plan
              </Badge>
            ) : isPro ? (
              <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800 shadow-none">
                <Crown className="h-3 w-3 mr-1" />
                Recommended
              </Badge>
            ) : null}
          </div>

          <div className="p-6 pb-2 relative">

            <div className="flex items-start gap-4">
              <div className={cn(
                "h-12 w-12 rounded-lg flex items-center justify-center shrink-0",
                isPro
                  ? "bg-primary/10 text-primary"
                  : "bg-muted text-muted-foreground"
              )}>
                {isPro ? (
                  <Zap className="h-6 w-6" />
                ) : (
                  <ShieldCheck className="h-6 w-6" />
                )}
              </div>
              <div className={cn(
                "flex-1 min-w-0 z-0",
                (isCurrentPlan || isPro) && "pr-6"
              )}>
                <CardTitle className="text-xl font-bold mb-1">{plan.name}</CardTitle>
                <CardDescription className="line-clamp-2 min-h-[40px]">{plan.description}</CardDescription>
              </div>
            </div>
          </div>


          <CardContent className="space-y-6 flex-1 flex flex-col">
            {/* Pricing Section */}
            <div className="py-4 border-t border-b border-border/50">
              <div className="flex items-baseline gap-1">
                <span className="text-3xl font-bold decoration-slice">₹{currentPrice}</span>
                <span className="text-muted-foreground text-sm font-medium">
                  /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                </span>
              </div>
              {billingCycle === 'yearly' && savings > 0 && (
                <div className="flex items-center gap-2 mt-1.5 text-xs font-semibold text-green-600 dark:text-green-500">
                  <Badge variant="outline" className="border-green-200 bg-green-50 text-green-700 dark:border-green-900/30 dark:bg-green-900/10 dark:text-green-400 px-1.5 py-0 h-5">
                    Save ₹{savings}/yr
                  </Badge>
                </div>
              )}
            </div>

            {/* Features List */}
            <div className="space-y-4 flex-1">
              <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                Features
              </div>
              <ul className="space-y-3">
                {plan.features.slice(0, isPro ? 8 : 6).map((feature: any, index: number) => (
                  feature.included && (
                    <li key={index} className="flex items-start gap-3 text-sm group/item">
                      <div className="mt-0.5 shrink-0 p-0.5 rounded-full bg-primary/10 text-primary">
                        <Check className="h-3 w-3" />
                      </div>
                      <span className="text-foreground/80 group-hover/item:text-foreground transition-colors">{feature.name}</span>
                    </li>
                  )
                ))}
              </ul>

              {/* AI Features Highlight for Pro */}
              {isPro && (
                <div className="mt-4 p-3 rounded-md bg-primary/5 border border-primary/10">
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="h-3.5 w-3.5 text-primary" />
                    <span className="text-xs font-bold text-primary">
                      AI Capabilities
                    </span>
                  </div>
                  <p className="text-[11px] text-muted-foreground leading-snug">
                    Includes advanced AI writing assistant, storytelling agent & smart plot suggestions.
                  </p>
                </div>
              )}
            </div>

            {/* Action Button */}
            <div className="mt-auto pt-4">
              {isCurrentPlan ? (
                <div className="space-y-3">
                  <Button disabled variant="outline" className="w-full bg-primary/5 border-primary/20 text-primary opacity-100 font-medium">
                    Current Plan
                  </Button>
                  {/* Show unsubscribe button only for Pro users */}
                  {!isTrialUser && currentPlan !== 'Free' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleUnsubscribe}
                      disabled={isUnsubscribing}
                      className="w-full h-8 text-muted-foreground hover:text-destructive hover:bg-destructive/10 text-xs"
                    >
                      {isUnsubscribing ? (
                        <>
                          <X className="h-3 w-3 mr-1 animate-spin" />
                          Cancelling...
                        </>
                      ) : (
                        "Cancel Subscription"
                      )}
                    </Button>
                  )}
                </div>
              ) : (
                <RazorpayPayment
                  plan={plan}
                  billingCycle={billingCycle}
                  onSuccess={onUpgradeSuccess}
                  className={cn(
                    "w-full font-semibold transition-all shadow-md active:scale-95",
                    isPro
                      ? "bg-primary hover:bg-primary/90 text-primary-foreground shadow-primary/20"
                      : "bg-secondary hover:bg-secondary/80 text-secondary-foreground"
                  )}
                >
                  {isPro ? "Upgrade to " + plan.name : "Choose " + plan.name}
                </RazorpayPayment>
              )}
            </div>

            {/* Renewal info for current plan in footer */}
            {isCurrentPlan && licenseDetails?.expiry_date && (
              <div className="text-center text-[10px] text-muted-foreground pt-2">
                {isTrialUser ? 'Trial expires' : 'Renews'} {new Date(licenseDetails.expiry_date).toLocaleDateString()}
              </div>
            )}

          </CardContent>
        </Card>
      </motion.div >
    );
  };

  return (
    <div className="space-y-10">

      {/* Overview Section */}
      <section>
        <h3 className="text-lg font-medium mb-4 flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-primary" />
          Subscription Status
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="border-border/60 bg-card/40 backdrop-blur-sm overflow-hidden group hover:border-primary/20 transition-colors">
            <CardContent className="p-6 flex items-center gap-5">
              <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                {currentPlan === 'Visionary' ? <Zap className="h-6 w-6" /> : <ShieldCheck className="h-6 w-6" />}
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">Current Plan</div>
                <div className="text-2xl font-bold text-foreground">{currentPlan}</div>
                <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  <div className={cn("h-1.5 w-1.5 rounded-full", isTrialUser ? "bg-amber-500" : "bg-green-500")} />
                  {isTrialUser
                    ? `Trial access`
                    : `Active subscription`}
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-border/60 bg-card/40 backdrop-blur-sm overflow-hidden group hover:border-primary/20 transition-colors">
            <CardContent className="p-6 flex items-center justify-between">
              <div className="flex items-center gap-5">
                <div className="h-14 w-14 rounded-full bg-secondary flex items-center justify-center text-muted-foreground shadow-inner group-hover:text-primary group-hover:bg-primary/10 transition-colors">
                  <Coins className="h-6 w-6" />
                </div>
                <div>
                  <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">Available Credits</div>
                  <div className="text-2xl font-mono font-bold text-foreground">{licenseDetails?.credits?.toLocaleString() || 0}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    Use specific AI models
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <Button variant="outline" size="sm" className="h-8 text-xs">
                  Top Up
                </Button>
                <Button variant="ghost" size="sm" className="h-8 text-xs text-muted-foreground">
                  <History className="h-3 w-3 mr-1" /> History
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Plans Section */}
      <section className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4 border-b border-border/40 pb-4">
          <div>
            <h3 className="text-xl font-bold">Available Plans</h3>
            <p className="text-muted-foreground">Choose the plan that best fits your creative needs.</p>
          </div>

          {/* Billing Cycle Selector - Styled */}
          <div className="bg-muted/50 p-1 rounded-xl inline-flex border border-border/50">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={cn(
                "px-4 py-1.5 text-sm font-medium rounded-lg transition-all duration-200",
                billingCycle === 'monthly'
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={cn(
                "px-4 py-1.5 text-sm font-medium rounded-lg transition-all duration-200 flex items-center gap-1.5",
                billingCycle === 'yearly'
                  ? "bg-background text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              Yearly
              <span className="text-[10px] font-bold bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 px-1.5 py-0.5 rounded-full">
                -17%
              </span>
            </button>
          </div>
        </div>

        {/* Trial Alert - Only show if urgent */}
        {isTrialUser && licenseDetails?.days_remaining !== undefined && licenseDetails.days_remaining <= 7 && (
          <Alert className="border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-100">
            <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
            <AlertDescription>
              Your trial expires in <span className="font-bold">{licenseDetails.days_remaining} days</span>.
              Upgrade now to keep your work and access all features.
            </AlertDescription>
          </Alert>
        )}

        {/* Plan Cards Grid */}
        <div className="grid gap-8 md:grid-cols-2 max-w-4xl mx-auto items-stretch">
          {(() => {
            const corePlan = SUBSCRIPTION_PLANS.find(p => p.id === 'core');
            const plusPlan = SUBSCRIPTION_PLANS.find(p => p.id === 'plus');

            // Logic to determine which plans are visible based on current status
            // If user is on Plus, show Plus (current) and maybe Core (downgrade - if we supported that visually)
            // For now, mirroring original logic: show both if trial/free, or specific ones if subscribed

            const plans = [];
            if (corePlan) plans.push({ plan: corePlan, isCurrentPlan: currentPlan === 'Plot Studio Core' });
            if (plusPlan) plans.push({ plan: plusPlan, isCurrentPlan: currentPlan === 'Plot Studio Plus' }); // Assuming 'Visionary' maps to Plus or we use 'Plot Studio Plus' string match

            // Verify mapping of plan names vs IDs from SUBSCRIPTION_PLANS constant vs licenseDetails.plan string
            // licenseDetails.plan is likely the *name* 'Plot Studio Core' or 'Plot Studio Plus'
            // SUBSCRIPTION_PLANS IDs are 'core', 'plus'

            return plans.map(({ plan, isCurrentPlan }) => (
              renderPlanCard(plan, isCurrentPlan)
            ));
          })()}
        </div>
      </section>
    </div>
  );
}


